import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  styled,
  useTheme,
  Grid,
  Checkbox,
  Button,
  useMediaQuery,
  GlobalStyles,
} from '@mui/material';
import { ArrowLeft, ArrowLeft2, InfoCircle, TickCircle } from 'iconsax-react';
import BaseButton from '../../components/common/BaseButton';
import BaseSelectBox from '../../components/common/BaseSelectBox';
import InputBox from '../../components/Input/InputBox';
import useUsersFacade from '../../store/profile/useUsersFacade';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import api from '../../utils/api';
import StepperProgress from './ProgressStepper';
import StepperLoading from './StepperLoading'; // Import the new loading component
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { useConfig } from '../../Hooks/useConfig';
import { getCookie, setCookie } from '../../utils/cookies';
import {
  BackButton,
  BackText,
  BenefitsRow,
  BenefitsRowItem,
  BenefitsRowItemTick,
  CheckboxOption,
  CheckboxOptionText,
  DoneBox,
  EmailVerifiedBox,
  FieldLabel,
  Heading,
  HiddenCheckbox,
  InputRow,
  MagAPILogo,
  OptionText,
  RadioGroupContainer,
  RadioOption,
  StepBox,
  StepContentWrapper,
  StepperContainer,
  StepperPageContainer,
  StepSubTitle,
  StepTitle,
  StepTwoInfo,
  StepTwoInfoText,
  UserTypeInputRow,
  UserTypeOptionText,
  UserTypeRadioGroupContainer,
  UserTypeRadioOption,
} from './style';
import usePlanFacade from '../../store/plan/usePlanFacade';
import { toast } from 'react-toastify';
import ActivateTrialModal from '../PricingPage/components/ActivateTrialModal';

const Stepper = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const history = useHistory();
  const { fetchUsers, userInfo } = useUsersFacade();
  // get plan
  const { planInfo, fetchPlan } = usePlanFacade();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const route = useLocation();
  const verificationStatus = route.search.replace('?', '');
  const { baseDomain, assistantUrl } = useConfig();

  // Add a ref to track if we've done the initial check
  const hasCheckedInitialState = useRef(false);
  // Add state to track if we're still determining route access
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // First step form state
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [userType, setUserType] = useState('Company');
  const [role, setRole] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const [isTrialActivated, setIsTrialActivated] = useState(false);

  // Service options for multi-select
  const serviceOptions = [
    { id: 'resume parser', name: 'Resume Parser' },
    { id: 'resume review', name: 'Resume Checker' },
    { id: 'resume score', name: 'Resume Matcher' },
    { id: 'profile data', name: 'LinkedIn Profile Scraper' },
    { id: 'company data', name: 'LinkedIn Company Scraper' },
    { id: 'all services', name: 'All Services' },
  ];

  // Handle checkbox selection
  const handleServiceToggle = (serviceId) => {
    if (serviceId === 'all services') {
      // If "All Services" is selected, check if it's already in the array
      if (selectedServices.includes('all services')) {
        setSelectedServices([]); // Deselect all
      } else {
        // Select all services
        setSelectedServices(['all services']);
      }
    } else {
      // Handle individual service selection
      const newSelectedServices = [...selectedServices];

      // Remove "All Services" if it was previously selected
      if (newSelectedServices.includes('all services')) {
        newSelectedServices.splice(
          newSelectedServices.indexOf('all services'),
          1
        );
      }

      // Toggle the selected service
      if (newSelectedServices.includes(serviceId)) {
        newSelectedServices.splice(newSelectedServices.indexOf(serviceId), 1);
      } else {
        newSelectedServices.push(serviceId);
      }

      setSelectedServices(newSelectedServices);
    }
  };

  const [characterCount, setCharacterCount] = useState(0);

  // Update the onChange handler for company name
  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    setCompanyName(value);
    setCharacterCount(value.length);

    // Clear error if field isn't empty
    if (value.trim()) {
      setCompanyNameError('');
    }

    // Add character limit validation
    if (value.length > 150) {
      setCompanyNameError('Company name must be 150 characters or less');
    }
  };

  // Role options for select box
  const roleOptions = [
    { name: "I'm a Founder/Owner", code: 'founder' },
    { name: 'Marketing', code: 'marketing' },
    { name: 'HR or Recruiting', code: 'hr' },
    { name: 'Operations', code: 'operations' },
    { name: 'CEO', code: 'ceo' },
    { name: 'CTO', code: 'cto' },
    { name: 'Other', code: 'other' },
  ];

  // Define the steps for the onboarding process
  const steps = [
    {
      title: t('stepper.firstStepTitle', 'Welcome to MagicalAPI'),
      label: 'Company Info', // Label for progress stepper
      content: (
        <Box sx={{ width: '100%' }}>
          <UserTypeInputRow>
            <UserTypeRadioGroupContainer>
              {['Company', 'Individual'].map((type) => (
                <UserTypeRadioOption
                  key={type}
                  selected={userType === type}
                  onClick={() => setUserType(type)}
                >
                  <UserTypeOptionText selected={userType === type}>
                    {type}
                  </UserTypeOptionText>
                </UserTypeRadioOption>
              ))}
            </UserTypeRadioGroupContainer>
          </UserTypeInputRow>

          <InputRow>
            <FieldLabel disabled={userType === 'Individual'}>
              Company Name
            </FieldLabel>
            <InputBox
              placeholder="Your company name"
              type="text"
              value={companyName}
              onChange={handleCompanyNameChange}
              ErrorInput={!!companyNameError}
              errorMassage={companyNameError}
              disabled={userType === 'Individual'}
            />
          </InputRow>

          <InputRow>
            <FieldLabel disabled={userType === 'Individual'}>
              Company Size
            </FieldLabel>
            <RadioGroupContainer>
              {['1-10', '11-50', '51-200', '201-500', '+500'].map(
                (size, index) => (
                  <RadioOption
                    key={size}
                    selected={companySize === size}
                    // onClick={() => setCompanySize(size)}
                    onClick={
                      userType === 'Individual'
                        ? (e) => e.preventDefault()
                        : () => setCompanySize(size)
                    }
                    disabled={userType === 'Individual'}
                  >
                    <OptionText
                      selected={companySize === size}
                      disabled={userType === 'Individual'}
                    >
                      {size}
                    </OptionText>
                  </RadioOption>
                )
              )}
            </RadioGroupContainer>
          </InputRow>

          <InputRow>
            <FieldLabel disabled={userType === 'Individual'}>
              Your Role
            </FieldLabel>
            <BaseSelectBox
              list={roleOptions}
              values={role}
              placeholder="Choose an option"
              selected={(selectedRole) => setRole(selectedRole)}
              disabled={userType === 'Individual'}
            />
          </InputRow>
        </Box>
      ),
    },
    {
      title: t('stepper.secondStepTitle', 'Tell us about your needs'),
      content: (
        <Box sx={{ width: '100%' }}>
          <StepTwoInfo>
            <InfoCircle size="19" color="#929094" />
            <StepTwoInfoText>
              You can choose more than one option.
            </StepTwoInfoText>
          </StepTwoInfo>
          <Grid container rowSpacing={mdDown ? 2 : 5} columnSpacing={4.5}>
            {serviceOptions.map((service) => (
              <Grid item xs={12} sm={4} key={service.id}>
                <CheckboxOption
                  selected={selectedServices.includes(service.id)}
                  onClick={() => handleServiceToggle(service.id)}
                >
                  <CheckboxOptionText
                    selected={selectedServices.includes(service.id)}
                  >
                    {service.name}
                  </CheckboxOptionText>
                  <HiddenCheckbox
                    checked={selectedServices.includes(service.id)}
                    onChange={() => {}}
                  />
                  {selectedServices.includes(service.id) ? (
                    <img
                      src="/static/img/icon/checkbox-icon.svg"
                      alt="Completed"
                    />
                  ) : null}
                </CheckboxOption>
              </Grid>
            ))}
          </Grid>
        </Box>
      ),
    },
    {
      title: t('stepper.thirdStepTitle'),
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <BenefitsRow>
            <BenefitsRowItem>
              <BenefitsRowItemTick
                src="/static/img/icon/charm_tick.svg"
                alt="Completed"
              />
              Access to API
            </BenefitsRowItem>
            <BenefitsRowItem>
              <BenefitsRowItemTick
                src="/static/img/icon/charm_tick.svg"
                alt="Completed"
              />
              2000 Credits
            </BenefitsRowItem>
            <BenefitsRowItem>
              <BenefitsRowItemTick
                src="/static/img/icon/charm_tick.svg"
                alt="Completed"
              />
              Available for 7 days
            </BenefitsRowItem>
          </BenefitsRow>
        </Box>
      ),
    },
  ];

  // Update the validateFirstStep function
  const validateFirstStep = () => {
    let isValid = true;

    if (!companyName.trim()) {
      setCompanyNameError('Company name is required');
      isValid = false;
    } else if (companyName.length > 150) {
      setCompanyNameError('Company name must be 150 characters or less');
      isValid = false;
    }

    return isValid && !!companySize && !!role;
  };

  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  const isPanel = subdomain.includes('panel');

  const tryPlaygroundOnClick = () => {
    // List of cookies to check
    const cookieNames = [
      'profile-data',
      'company-data',
      'resume-review',
      'resume-parser',
      'resume-score',
    ];

    // Check for any of these cookies
    let foundCookie = null;
    for (const cookieName of cookieNames) {
      const cookieValue = getCookie(cookieName, baseDomain);
      if (cookieValue) {
        foundCookie = cookieName;
        break;
      }
    }

    // Determine navigation path based on cookie
    if (foundCookie) {
      if (isPanel) {
        window.location.href = `${playgroundUrl}/${foundCookie}`;
      } else {
        history.push(`/${foundCookie}`);
      }
    } else {
      // Original behavior if no cookie is found
      if (isPanel) {
        window.location.href = `${playgroundUrl}`;
      } else {
        history.push('/');
      }
    }
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    if (currentStep === 0) {
      if (userType === 'Individual') {
        return true;
      } else {
        return !!companyName && !!companySize && !!role && !!userType;
      }
    } else if (currentStep === 1) {
      return selectedServices.length > 0;
    }
    return true;
  };

  // Function to handle submitting user preferences
  const submitPreferences = async () => {
    setIsLoading(true);
    try {
      const stepperInfoReq = await api.call({
        name: 'submitStepperInfo',
        data: {
          company_name: userType === 'Individual' ? 'Individual' : companyName,
          company_size: companySize,
          user_role: role,
          product_selection: selectedServices.join(','),
        },
      });

      if (stepperInfoReq.status === 200) {
        setCurrentStep(2);
        await fetchUsers();
      }
    } catch (error) {
      console.error('Failed to submit preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const { panelUrl, playgroundUrl } = useConfig();

  // Handle next button click based on current step
  const handleNext = () => {
    if (currentStep === 0) {
      if (userType === 'Individual') {
        setCurrentStep(1);
      } else {
        if (validateFirstStep()) {
          setCurrentStep(1);
        }
      }
    } else if (currentStep === 1) {
      if (selectedServices.length > 0) {
        submitPreferences();
      }
    } else if (currentStep === 2 && isTrialActivated) {
      tryPlaygroundOnClick();
    } else if (currentStep === 2) {
      setIsTrialModalOpen(true);
    }
  };

  // Handle back action
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle "Go to panel" action
  const handleGoToPanel = () => {
    if (isPanel) {
      history.push('/');
    } else {
      window.location.href = `${panelUrl}`;
    }
  };

  // Render back button or "Go to panel" text based on current step
  const renderBackButton = () => {
    if (currentStep === 0) {
      return null;
    } else if (currentStep === 1) {
      return (
        <BackButton onClick={handleBack}>
          {mdDown ? (
            <ArrowLeft2 size={18} color="#A0A0A0" />
          ) : (
            <img src="/static/img/icon/arrow-left-gray.svg" alt="Back_Button" />
          )}
        </BackButton>
      );
    } else if (currentStep === 2 && !isTrialActivated) {
      return (
        <BackButton onClick={handleGoToPanel}>
          <BackText>Go to panel</BackText>
        </BackButton>
      );
    }
    // Return null if trial is activated (don't show back button or go to panel)
    return null;
  };

  // Get the label for the primary action button
  const getActionButtonLabel = () => {
    if (currentStep === 0) {
      return t('button_text.continue', 'Continue');
    } else if (currentStep === 1) {
      return t('button_text.continue', 'Continue');
    } else if (currentStep === 2 && isTrialActivated) {
      return 'Try the Playground';
    } else {
      return t('button_text.finish', 'Activate Free Trial!');
    }
  };

  // Updated useEffect with proper loading state management
  useEffect(() => {
    // Only perform the redirect check if we haven't checked the initial state yet
    // and userInfo is available (not empty/loading)
    if (
      !hasCheckedInitialState.current &&
      userInfo &&
      Object.keys(userInfo).length > 0
    ) {
      // Mark that we've done the initial check
      hasCheckedInitialState.current = true;

      // Only redirect if show_stepper is false or email is not verified
      if (!userInfo.show_stepper || userInfo.email_verified_at === null) {
        setCookie('showStepper', 'false', 34560000, baseDomain);
        history.push('/');
      } else {
        // User should stay on this route, stop showing loading
        setIsCheckingAccess(false);
      }
    } else if (userInfo && Object.keys(userInfo).length === 0) {
      // If userInfo is empty object, we're still loading
      setIsCheckingAccess(true);
    }
  }, [userInfo, history]);

  useEffect(() => {
    if (userType === 'Individual') {
      setCompanySize('');
      setCompanyName('');
      setRole('');
    }
  }, [userType]);

  // Show loading component while checking access
  if (isCheckingAccess) {
    return <StepperLoading />;
  }

  return (
    <StepperPageContainer>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <GlobalStyles
        styles={{
          html: {
            backgroundColor: `${theme.palette.secondary.paper} !important`,
          },
          body: {
            backgroundColor: `${theme.palette.secondary.paper} !important`,
          },
        }}
      />

      <StepperContainer>
        {/* Logo header */}
        <Box sx={{ textAlign: 'center' }}>
          <MagAPILogo
            src={`/static/img/${theme.palette.mode === 'dark' ? 'magicalapi_logo_white.svg' : 'magicalApi-icon.svg'}`}
            alt="MagicalAPI"
          />
        </Box>
        <Heading>{t('stepper.heading', 'Complete Your Account Setup')}</Heading>
        <StepBox>
          {renderBackButton()}

          {currentStep === 2 && (
            <DoneBox>
              <TickCircle size={mdDown ? 20 : 32} />
              {isTrialActivated
                ? 'Your Free Trial is Activated!'
                : `You're all set!`}
            </DoneBox>
          )}

          {currentStep === 0 &&
          verificationStatus === 'email-verification-successful' ? (
            <EmailVerifiedBox>
              <TickCircle size={mdDown ? 20 : 32} />
              Your email has been verified
            </EmailVerifiedBox>
          ) : null}

          <StepperProgress
            currentStep={currentStep}
            totalSteps={steps.length}
          />

          <StepTitle>{steps[currentStep].title}</StepTitle>
          {steps[currentStep]?.subTitle && (
            <StepSubTitle>{steps[currentStep].subTitle}</StepSubTitle>
          )}

          <StepContentWrapper>{steps[currentStep].content}</StepContentWrapper>

          <BaseButton
            onClick={handleNext}
            loading={isLoading}
            disabled={!isCurrentStepValid()}
            sx={{ width: '100%' }}
            size="medium"
          >
            {getActionButtonLabel()}
          </BaseButton>

          {currentStep === 2 && isTrialActivated && (
            <BaseButton
              onClick={handleGoToPanel}
              sx={{
                width: '100%',
                backgroundColor: 'transparent',
                color: '#0486D7',
                mt: mdDown ? 2 : 4,
                '&:hover': {
                  backgroundColor: '#EFEFEF',
                },
              }}
            >
              Go to Panel
            </BaseButton>
          )}

          {currentStep === 2 && !isTrialActivated && (
            <BaseButton
              onClick={tryPlaygroundOnClick}
              variant="transparent"
              sx={{
                width: '100%',
                mt: mdDown ? 2 : 4,
              }}
            >
              Try the Playground
            </BaseButton>
          )}
        </StepBox>
      </StepperContainer>

      <ActivateTrialModal
        open={isTrialModalOpen}
        handleClose={() => setIsTrialModalOpen(false)}
        onTrialActivated={() => setIsTrialActivated(true)}
      />
    </StepperPageContainer>
  );
};

export default Stepper;
